import type { GraphNode, OpportunityFocus, OpportunityOption } from '../../types/workbench'

// The opportunity the workbench serves (REQ-121), as pure helpers: the route carries it as ?opportunity=, a chat
// reply's effect may name one, and an opening's people are matched to the stage's person nodes when it is made
// an opportunity. No React, no fetch, no arithmetic beyond counting what the API already sent.

export const OPPORTUNITY_PARAM = 'opportunity'

/** The same search params with the opportunity set, or removed when null. Every other param is kept. */
export function withOpportunity(params: URLSearchParams, engagementId: string | null): URLSearchParams {
  const next = new URLSearchParams(params)
  if (engagementId) next.set(OPPORTUNITY_PARAM, engagementId)
  else next.delete(OPPORTUNITY_PARAM)
  return next
}

/** What a reply's `opportunity` effect names: a stored opportunity when it is one of the account's pursuits, else the derived opening it is. */
export function effectOpportunity(id: string, pursuits: OpportunityOption[] | undefined): { engagementId: string } | { openingId: string } {
  return (pursuits ?? []).some((p) => p.engagementId === id) ? { engagementId: id } : { openingId: id }
}

const fold = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ')

/** The person ids on the stage whose label is one of the names, matched case- and space-insensitively; unknown names are dropped, order kept, no duplicates. */
export function matchPeople(names: string[], nodes: GraphNode[]): string[] {
  const byLabel = new Map<string, string>()
  for (const n of nodes) if (n.kind === 'person' && !n.background && !byLabel.has(fold(n.label))) byLabel.set(fold(n.label), n.personId)
  const out: string[] = []
  for (const name of names) {
    const id = byLabel.get(fold(name))
    if (id && !out.includes(id)) out.push(id)
  }
  return out
}

/** "3 messages · 1 play" — the counts under the chosen opportunity, in words. */
export function countsLine(focus: Pick<OpportunityFocus, 'correspondences' | 'tactics'>): string {
  const n = focus.correspondences.length, t = focus.tactics.length
  return `${n} ${n === 1 ? 'message' : 'messages'} · ${t} ${t === 1 ? 'play' : 'plays'}`
}

/** A summary longer than this folds behind "more" on the panel. */
export const SUMMARY_FOLD = 220

/** The summary's first sentence or so when it is long, else the whole of it; `folded` says whether anything was held back. */
export function foldSummary(summary: string, limit = SUMMARY_FOLD): { text: string; folded: boolean } {
  const s = summary.trim()
  if (s.length <= limit) return { text: s, folded: false }
  const cut = s.slice(0, limit)
  const end = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf(' '))
  return { text: `${cut.slice(0, end > 40 ? end : limit).trimEnd()}…`, folded: true }
}
